import { useState, useEffect } from 'react';
import { X, Loader2, UserSearch } from 'lucide-react';
import { OrgUserWithRole } from '@property/shared';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useAllUserSearch } from '@/hooks/use-agent-queries';
import { useDebouncedUserSearch } from '@/hooks/use-debounced-user-search';

export function UserAvatar({ user, size = 'sm' }: { user: OrgUserWithRole; size?: 'sm' | 'md' }) {
  const dim = size === 'md' ? 'h-7 w-7' : 'h-5 w-5';
  const initials = (user.name ?? user.email)
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  if (user.profilePictureUrl) {
    return (
      <img
        src={user.profilePictureUrl}
        alt={user.name ?? user.email}
        className={cn(dim, 'rounded-full object-cover flex-shrink-0')}
      />
    );
  }

  return (
    <span
      className={cn(
        dim,
        'rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-semibold flex-shrink-0',
      )}
    >
      {initials}
    </span>
  );
}

export function AssigneeSearchInput({
  selectedUser,
  onSelect,
  onClear,
  dropdownDirection = 'down',
}: {
  selectedUser: OrgUserWithRole | null;
  onSelect: (user: OrgUserWithRole) => void;
  onClear: () => void;
  dropdownDirection?: 'up' | 'down';
}) {
  const { query, setQuery, debouncedQuery, reset } = useDebouncedUserSearch(300);
  const [open, setOpen] = useState(false);

  const { data: results, isFetching } = useAllUserSearch(debouncedQuery);

  // Mirrors the debounce landing (not every keystroke) — same timing as when this set
  // `open` directly inside its own debounce timeout callback. Blur/focus below still
  // adjust `open` independently (close shortly after blur, reopen on refocus if the
  // debounced query already qualifies).
  useEffect(() => {
    setOpen(debouncedQuery.length >= 3);
  }, [debouncedQuery]);

  function handleSelect(user: OrgUserWithRole) {
    onSelect(user);
    reset();
    setOpen(false);
  }

  if (selectedUser) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium w-fit">
        <span>{selectedUser.name ?? selectedUser.email}</span>
        <UserAvatar user={selectedUser} size="sm" />
        <button
          onClick={onClear}
          className="ml-1 rounded-full hover:bg-primary/20 p-0.5 transition-colors"
          aria-label="Clear selection"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  const dropdownClass =
    dropdownDirection === 'up'
      ? 'absolute z-10 bottom-full mb-1 w-full rounded-lg border border-border/60 bg-background shadow-lg overflow-hidden max-h-52 overflow-y-auto'
      : 'absolute z-10 top-full mt-1 w-full rounded-lg border border-border/60 bg-background shadow-lg overflow-hidden max-h-52 overflow-y-auto';

  return (
    <div className="relative w-full">
      <div className="relative">
        <UserSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="border-border-strong bg-card pl-9 pr-9 text-base"
          placeholder="Search by name or email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onFocus={() => debouncedQuery.length >= 3 && setOpen(true)}
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
        )}
      </div>
      {open && results !== undefined && (
        <div className={dropdownClass}>
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">No users found</div>
          ) : (
            results.map((user) => (
              <button
                key={user.id}
                className="w-full flex items-center justify-between gap-3 text-left px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors"
                onMouseDown={() => handleSelect(user)}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{user.name ?? user.email}</div>
                  {user.name && (
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  )}
                </div>
                <UserAvatar user={user} size="md" />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
